import qs from 'qs'
import {
  API_CALL, CRUISE_IN_FLOW_CHECKOUT_STEP,
  RESET_REVELEX_CALLS,
  RESET_CABIN_AVAILABILITY,
  SAVE_CABIN_OPTION,
  RESTORE_CABIN_OPTION,
  CRUISE_SET_SEARCH_FACETS,
} from './actionConstants'
import { CruisesContract } from '@luxuryescapes/contract-svc-cruise'
import { expireCruiseTimer, resetCruiseTimer } from 'components/Cruises/CruiseSessionTimer/CruiseSessionTimer'
import { cruiseCheckoutConfirmCabin, cruisesAddToCartExperiment } from 'analytics/eventDefinitions'
import { fireABTestGAEvent, fireInteractionEvent } from 'api/googleTagManager'
import GobalSearchState from 'contexts/GlobalSearch/GlobalSearchState'
import {
  FETCH_CRUISE_OFFER, FETCH_CRUISES_LIST_RECOMMENDATION, FETCH_CRUISE_SHIP_DECK,
  FETCH_CRUISE_CABIN_RATES,
  FETCH_CRUISE_SEARCH_COUNT,
  FETCH_CRUISE_SEARCH_FACETS,
  FETCH_CRUISE_CABIN_HOLD,
  FETCH_CRUISE_CABIN_RELEASE,
  FETCH_CRUISE_CABIN_PRICING,
  FETCH_CRUISE_CABIN_DETAILS,
  FETCH_CRUISE_CABIN_AVAILABILITY,
  FETCH_CRUISE_CABIN_RATE_DETAILS,
  FETCH_CRUISE_BOOKING_BY_ID,
  FETCH_CRUISE_DEPARTURE_BY_ID,
  FETCH_CRUISE_LINES,
  FETCH_CRUISE_PROMO_BANNERS,
  FETCH_CRUISE_LOWEST_PRICE_BY_MONTH,
  RESET_SESSION_ID,
} from './apiActionConstants'
import { getCruiseItems } from 'checkout/selectors/view/cruise'
import {
  bookingCabinDetails,
  bookingCabinHold,
  bookingCabinPricing,
  bookingCabinRateDetails,
  bookingCabinRates,
  bookingCabinRelease,
  cabinAvailability,
  getCruiseBookingById,
  getCruiseSearchFacets,
  getCruiseDepartureById,
  getCruiseLines,
  getCruiseLowestPriceByMonth,
  getCruiseOffer,
  getCruiseSearchList,
  getCruiseShipDeck,
  getCruisePromoBanners,
  getCruiseSearchCount,
} from 'api/cruises'
import getObjectKey from 'lib/object/getObjectKey'
import { navigateToCheckout } from 'checkout/actions/navigateActions'
import { pushWithRegion } from 'actions/NavigationActions'

const AVAILABLE_REGIONS = new Set(['AU', 'NZ'])

export function fetchCruiseLines(filter?: CruisesContract.OfferParams) {
  return (dispatch, getState) => {
    const state = getState() as App.State
    const region = state.geo.currentRegionCode

    if (region && !AVAILABLE_REGIONS.has(region.toUpperCase())) return

    if (!state.cruise.cruiseLines.length && !state.cruise.fetchingCruiseLines) {
      dispatch({
        type: API_CALL,
        api: FETCH_CRUISE_LINES,
        request: () => getCruiseLines(filter, region),
      })
    }
  }
}

export function fetchCruiseSearchListRecommendation() {
  return {
    type: API_CALL,
    api: FETCH_CRUISES_LIST_RECOMMENDATION,
    request: () => getCruiseSearchList({}),
  }
}

export function fetchCruiseOffer(offerId: string) {
  return (dispatch, getState) => {
    const state: App.State = getState()
    const currency = state.geo.currentCurrency
    const regionCode = state.geo.currentRegionCode

    if (state.cruise.cruiseOffersLoading[offerId] ||
      state.cruise.cruiseOffers[offerId]) {
      return
    }

    dispatch({
      offerId,
      type: API_CALL,
      api: FETCH_CRUISE_OFFER,
      request: () => getCruiseOffer(offerId, regionCode, currency),
    })
  }
}

export function fetchCruiseSearchCount(params: App.OfferListFilters) {
  return (dispatch, getState) => {
    const state = getState() as App.State
    const key = getObjectKey(params)

    if (state.cruise.cruiseSearchCount?.[key]) {
      // already have it or are currently fetching it
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_CRUISE_SEARCH_COUNT,
      request: () => getCruiseSearchCount(params, state.geo.currentRegionCode),
      key,
    })
  }
}

export function fetchCruiseSearchFacets(params: App.CruiseSearchFacetParams) {
  return (dispatch, getState) => {
    const key = getObjectKey(params)
    const state = getState() as App.State
    if (state.cruise.cruiseFacets[key]) {
      // already have it or are currently fetching it
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_CRUISE_SEARCH_FACETS,
      request: () => getCruiseSearchFacets(params, state.geo.currentRegionCode),
      key,
    })
  }
}

export function fetchCruiseLowestPriceByMonth(
  params: App.LowestPriceByMonthParams,
) {
  return (dispatch, getState) => {
    const state = getState() as App.State
    const key = getObjectKey(params)
    if (state.cruise.cruiseLowestPricesByMonth[key]) {
      // we're already fetching or have already fetched it
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_CRUISE_LOWEST_PRICE_BY_MONTH,
      request: () => getCruiseLowestPriceByMonth(params, state.geo.currentRegionCode),
      key,
    })
  }
}

export function fetchCruiseShipDeck(deckId: string) {
  return (dispatch, getState: () => App.State) => {
    const state = getState()

    if (state.cruise.deckSelections && deckId in state.cruise.deckSelections) { return }

    dispatch({
      deckId,
      type: API_CALL,
      api: FETCH_CRUISE_SHIP_DECK,
      request: () => getCruiseShipDeck(deckId),
    })
  }
}

export function fetchCruisePromoBanners() {
  return (dispatch, getState) => {
    const state = getState() as App.State
    if (!state.cruise.cruisePromoBanners?.length && !state.cruise.fetchingCruisePromoBanners) {
      const region = state.geo.currentRegionCode
      dispatch({
        type: API_CALL,
        api: FETCH_CRUISE_PROMO_BANNERS,
        request: () => getCruisePromoBanners(region),
      })
    }
  }
}

export function fetchCabinRates(params: CruisesContract.CabinRateBody) {
  return (dispatch, getState) => {
    expireCruiseTimer()
    const state = getState() as App.State
    const currency = state.geo.currentCurrency
    const region = state.geo.currentRegionCode
    if (state.cruise.cabinRatesLoading) return

    dispatch({
      type: API_CALL,
      api: FETCH_CRUISE_CABIN_RATES,
      travelers: params.travelers,
      departureId: params.departureId,
      request: () => bookingCabinRates(params, currency, region),
    })
  }
}

/**
 * Overridden 'sessionId' comes from the new checkout
 */
export function fetchCabinRateDetails(componentId: string, sessionId?: string) {
  return (dispatch, getState) => {
    const state = getState() as App.State
    const { cabinRateDetailsLoading, cabinRateDetails, revelexSessionId } = state.cruise
    const region = state.geo.currentRegionCode
    if (cabinRateDetailsLoading && cabinRateDetailsLoading[componentId]) return
    if (cabinRateDetails && cabinRateDetails[componentId]?.rateDetails.length > 0) return
    if (!revelexSessionId && !sessionId) return

    dispatch({
      componentId,
      type: API_CALL,
      api: FETCH_CRUISE_CABIN_RATE_DETAILS,
      request: () => bookingCabinRateDetails({ componentId, sessionId: revelexSessionId ?? sessionId }, region),
    })
  }
}

/**
 * // TODO
 * Better to not put this in the actions file.
 * You can create a dedicate utility library file here:
 * src/checkout/lib/utils
 * See the src/checkout/lib/utils/flights/ example
 */
export function fetchCabinAvailability(
  params: CruisesContract.CabinAvailabilityBody,
  revelexSessionId:string,
) {
  return (dispatch, getState) => {
    const state = getState() as App.State
    const { categoryId } = params
    const { cabinAvailability: cabins } = state.cruise
    const currency = state.geo.currentCurrency
    const region = state.geo.currentRegionCode

    if (!revelexSessionId) return
    if (!!cabins && cabins?.length > 0) return
    if (state.cruise.cabinAvailabilityLoading) return
    resetCruiseTimer()

    dispatch({
      categoryId,
      type: API_CALL,
      api: FETCH_CRUISE_CABIN_AVAILABILITY,
      request: () => cabinAvailability(params, revelexSessionId, currency, region),
    })
  }
}

export function fetchCabinHold(params: CruisesContract.CabinHoldBody, revelexSessionId: string) {
  return (dispatch, getState) => {
    const state = getState() as App.State
    const currency = state.geo.currentCurrency
    const region = state.geo.currentRegionCode
    resetCruiseTimer()

    if (!state.cruise.cabinHoldLoading) {
      dispatch({
        type: API_CALL,
        api: FETCH_CRUISE_CABIN_HOLD,
        request: () => bookingCabinHold(params, revelexSessionId, currency, region),
      })
    }
  }
}

export function fetchCabinRelease(sessionId?: string) {
  return (dispatch, getState) => {
    const state = getState() as App.State
    const { revelexSessionId } = state.cruise
    const region = state.geo.currentRegionCode
    expireCruiseTimer()

    dispatch({
      type: API_CALL,
      api: FETCH_CRUISE_CABIN_RELEASE,
      request: () => bookingCabinRelease(sessionId || revelexSessionId, region),
    })
  }
}

export function fetchCabinDetails(params: CruisesContract.CabinDetailsBody) {
  return (dispatch, getState) => {
    const state = getState() as App.State
    const { revelexSessionId } = state.cruise
    const region = state.geo.currentRegionCode

    if (!state.cruise.cabinDetailsLoading) {
      dispatch({
        type: API_CALL,
        api: FETCH_CRUISE_CABIN_DETAILS,
        request: () => bookingCabinDetails(params, revelexSessionId, region),
      })
    }
  }
}

export function fetchCabinPricing(params: CruisesContract.CabinPricingBody) {
  return (dispatch, getState) => {
    const state = getState() as App.State
    const revelexSessionId = getCruiseItems(state)[0].sessionId
    const currency = state.geo.currentCurrency
    const region = state.geo.currentRegionCode

    if (!state.cruise.cabinPricingLoading && revelexSessionId) {
      dispatch({
        type: API_CALL,
        api: FETCH_CRUISE_CABIN_PRICING,
        request: () => bookingCabinPricing(params, revelexSessionId, currency, region),
      })
    }
  }
}

export function saveCabinOptions(data?: App.CruiseCabinPreferences) {
  return {
    type: SAVE_CABIN_OPTION,
    data,
  }
}

export function restoreCabinOptions(data?: App.CruiseCabinPreferences) {
  return {
    type: RESTORE_CABIN_OPTION,
    data,
  }
}

export function addCabinToCart(cabinNumber: string) {
  return (dispatch) => {
    fireInteractionEvent(cruiseCheckoutConfirmCabin(cabinNumber))
    dispatch(navigateToCheckout())
    fireABTestGAEvent(cruisesAddToCartExperiment(), false)
  }
}

export function resetSessionId() {
  return {
    type: RESET_SESSION_ID,
  }
}

export function resetCabinRates() {
  return {
    type: RESET_REVELEX_CALLS,
  }
}

export function resetCabinAvailability() {
  return {
    type: RESET_CABIN_AVAILABILITY,
  }
}

export function fetchCruiseBookingById(bookingId: string) {
  return (dispatch, getState) => {
    const state = getState() as App.State
    const { cruiseBookingInfoLoading, cruiseBookingInfo } = state.cruise

    if (cruiseBookingInfo[bookingId] ||
      cruiseBookingInfoLoading && cruiseBookingInfoLoading[bookingId]) return

    dispatch({
      id: bookingId,
      type: API_CALL,
      api: FETCH_CRUISE_BOOKING_BY_ID,
      request: () => getCruiseBookingById(bookingId),
    })
  }
}

export function fetchCruiseDepartureById(departureId: string) {
  return (dispatch, getState) => {
    const state = getState() as App.State
    const { cruiseDepartureLoading, cruiseDeparture } = state.cruise

    if (cruiseDeparture[departureId] ||
      cruiseDepartureLoading && cruiseDepartureLoading[departureId]) return

    dispatch({
      departureId,
      type: API_CALL,
      api: FETCH_CRUISE_DEPARTURE_BY_ID,
      request: () => getCruiseDepartureById(departureId),
    })
  }
}

export function setInFlowCheckoutStep(data?: string) {
  return {
    type: CRUISE_IN_FLOW_CHECKOUT_STEP,
    data,
  }
}

export function addFlashCruiseFacets(
  params: App.CruiseSearchFacetParams,
  facets: App.CruiseSearchFacet[],
) {
  return (dispatch, getState) => {
    const state = getState() as App.State
    const key = getObjectKey(params)

    const uniqueFacets = [...facets, ...(state.cruise.cruiseFacets[key]?.facets || [])].reduce((acc, facet) => {
      const { name, category } = facet
      const key = `${name}_${category}`
      const exists = acc[key]

      if (exists) {
        if (facet.category === 'cruise_prices') {
          acc[key] = { ...facet, max: Math.max(exists.max || 0, facet.max || 0) }
        } else {
          acc[key] = { ...facet, flashOfferCount: exists.flashOfferCount }
        }
      } else {
        acc[key] = facet
      }

      return acc
    }, {} as Record<string, App.CruiseSearchFacet>)

    dispatch({
      type: CRUISE_SET_SEARCH_FACETS,
      key,
      data: Object.values(uniqueFacets).sort((a, b) => {
        const aValue = a.flashOfferCount ? 2 : 1
        const bValue = b.flashOfferCount ? 2 : 1
        return aValue - bValue
      }),
    })
  }
}

function getCruiseExtraFilters(queryParams: URLSearchParams) {
  const cruiseShips = queryParams.getAll('cruiseShips')
  const cabinTypes = queryParams.getAll('cabinTypes')
  const minPrice = queryParams.get('price.min')
  const maxPrice = queryParams.get('price.max')
  const isSpecialOffers = queryParams.get('isSpecialOffers')
  const isLuxExclusivePromotion = queryParams.get('isLuxExclusivePromotion')

  return {
    cruiseShips,
    cabinTypes,
    isSpecialOffers: Boolean(isSpecialOffers) || undefined,
    isLuxExclusivePromotion: Boolean(isLuxExclusivePromotion) || undefined,
    'price.min': minPrice ? parseInt(minPrice) : undefined,
    'price.max': maxPrice ? parseInt(maxPrice) : undefined,
  }
}

function getCruiseGlobalFilters(globalSearch: GobalSearchState) {
  const {
    searchItem,
    searchItems,
    secondarySearchItem,
    cruiseLines,
    flexibleMonths,
    durationMax,
    durationMin,
  } = globalSearch

  return {
    cruiseLines,
    durationMax: durationMax || undefined,
    durationMin: durationMin || undefined,
    destinationId: searchItem?.value,
    destinationName: searchItem?.format.mainText,
    portId: secondarySearchItem?.value,
    portName: secondarySearchItem?.format.mainText,
    departureMonths: flexibleMonths || undefined,
    destinationNames: searchItems.map((item) => item.format.mainText),
    destinationIds: searchItems.map((item) => item.value),
  }
}

function getCruiseFilters(globalSearch: GobalSearchState, queryParams?: URLSearchParams) {
  const globalFilters = getCruiseGlobalFilters(globalSearch)

  return {
    ...globalFilters,
    ...(queryParams ? getCruiseExtraFilters(queryParams) : {}),
  }
}

export function pushWithCruiseFilters(
  path: string,
  globalSearch: GobalSearchState,
  queryParams?: URLSearchParams,
) {
  return (dispatch) => {
    const filters = getCruiseFilters(globalSearch, queryParams)
    const query = qs.stringify(filters, { arrayFormat: 'repeat' })
    dispatch(pushWithRegion(path, query))
  }
}
